import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Renderer2
} from '@angular/core';
import { Subscription } from 'rxjs/Subscription';
import { HtmlDragSupport } from 'buzzyears.ui';
import {
  InterfaceVertex, SubWorkflowVertex,
  VertexInteractionPointLocation,
  VertexInteractionPointPreferredType,
  VertexInteractionPointSpecification,
  WorkflowVertex,
  WorkflowVertexSpecification,
  WorkflowVertexVisualization
} from 'component.workflow.core';
import { AbstractWorkflowGraphDispatcherObserver } from '../abstract-workflow-graph-dispatcher-observer';
import {
  WorkflowGraphDispatcherAction,
  WorkflowGraphDispatcherActionType as ActionType,
  WorkflowGraphDispatcherService
} from '../workflow-graph-dispatcher.service';
import { WorkflowGraphService } from '../workflow-graph.service';
import { WorkflowGraphSelectionService } from '../workflow-graph-selection.service';

@Component({
  selector: 'g[wf-graph-vertex]',
  template: `

    <svg:g [attr.transform]="transform" class="graph-vertex">

      <svg:g (mousedown)="onMouseDown($event)" (dblclick)="onDblClick($event)">
        <svg:path [attr.d]="vis.shape.representation.path.join(' ')" stroke-linecap="round"
                  [attr.stroke]="backgroundOverride(vis.stroke.fill)" [attr.stroke-width]="vis.stroke.width"
                  [attr.fill]="backgroundOverride(vis.shape.fill)"/>

        <svg:text [attr.x]="vis.text.primary.at.x" text-anchor="middle" [attr.y]="vis.text.primary.at.y"
                  [attr.font-size]="vis.text.primary.size"
                  [attr.fill]="foregroundOverride(vis.text.primary.fill)">{{ specifications.abbr }}
        </svg:text>

        <svg:text [attr.x]="vis.text.secondary.at.x" [attr.y]="vis.text.secondary.at.y" text-anchor="middle"
                  [attr.font-size]="vis.text.secondary.size"
                  font-weight="bold"
                  [attr.fill]="foregroundOverride(vis.text.secondary.fill)">
          {{ name }}
        </svg:text>
      </svg:g>

      <svg:g class="points">
        <svg:g *ngFor="let point of vertex.inlets; let i = index" wf-graph-vertex-inlet-point [vertex]="vertex"
               [point]="point" [editable]="editable" [specification]="getInletSpecification(i)"/>

        <svg:g *ngFor="let point of vertex.outlets; let i = index" wf-graph-vertex-outlet-point [vertex]="vertex"
               [point]="point" [editable]="editable" [specification]="getOutletSpecification(i)"/>
      </svg:g>
    </svg:g>

  `,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class WorkflowGraphVertexComponent extends AbstractWorkflowGraphDispatcherObserver implements OnInit, OnDestroy, OnChanges {

  @Input() vertex: WorkflowVertex;
  @Input() zoom = 1;
  @Input() editable = true;

  protected dragSupport: HtmlDragSupport;

  dragMove$: Subscription;
  dragFinish$: Subscription;
  selectionUpdated$: Subscription;

  specifications: WorkflowVertexSpecification;

  selected = false;
  inMultiSelected = false;

  constructor(protected renderer: Renderer2,
              dispatcherService: WorkflowGraphDispatcherService,
              protected cdr: ChangeDetectorRef,
              protected graphSelectionService: WorkflowGraphSelectionService,
              protected graphService: WorkflowGraphService) {

    super(dispatcherService);
    this.dragSupport = new HtmlDragSupport(renderer);

    this.dragMove$ = this.dragSupport.dragMove.subscribe(location => this.setLocation(location));
    this.dragFinish$ = this.dragSupport.dragFinish.subscribe(() => this.onDragFinish());

    this.selectionUpdated$ = this.graphSelectionService.selectionUpdated.subscribe((selection) => this.onSelectionUpdated(selection));
  }

  onMouseDown(event: MouseEvent) {

    if (event.which !== 1) {
      // If its not a left click, ignore it.
      return;
    }

    if (event.shiftKey) {
      // Its a control click
      this.dispatcherService.dispatchAction(ActionType.MultiSelectVertex, this, [this.vertex]);
      return;
    }

    if (this.editable) {
      const origin = {x: +this.vertex.x, y: +this.vertex.y};

      if (this.graphSelectionService.isSelected(this.vertex)) {
        this.graphSelectionService.selectionDragActivated(event, origin, this.zoom);
      }

      this.dragSupport.activate(event, origin, this.zoom);
    }

    event.stopPropagation();
    return false;
  }

  onDblClick(event: MouseEvent) {
    this.dispatcherService.dispatchAction(ActionType.SelectVertex, this, this.vertex);
  }

  setLocation(location: any) {

    if (this.graphSelectionService.isSelected(this.vertex)) {
      // Pass it on to the service
      this.graphSelectionService.selectionDragMoved(location);
      this.repaint();
      return;
    }

    this.vertex.x = parseFloat(location.x.toFixed(3));
    this.vertex.y = parseFloat(location.y.toFixed(3));

    this.dispatcherService.dispatchAction(ActionType.VertexMoving, this, this.vertex);
    this.repaint();
  }

  onDragFinish() {
    if (this.graphSelectionService.isSelected(this.vertex)) {
      // Pass it on to the service
      this.graphSelectionService.selectionDragFinished();
      return;
    }

    this.dispatcherService.dispatchAction(ActionType.VertexMoved, this, this.vertex);
  }

  get name(): string {
    return (this.vertex instanceof InterfaceVertex || this.vertex instanceof SubWorkflowVertex) ?
      this.vertex.name : this.specifications.name;
  }

  get transform(): string {
    const x = this.vertex.x, y = this.vertex.y;
    return `translate(${x}, ${y})`;
  }

  selectIfApplicable(v: WorkflowVertex) {
    const selected = v === this.vertex;

    if (selected !== this.selected) {
      this.selected = selected;
      this.repaint();
    }
  }

  repaint() {
    this.cdr.markForCheck();
  }

  onSelectionUpdated(vertices: WorkflowVertex[]) {
    const inMultiSelected = vertices.findIndex(v => this.vertex === v) >= 0;
    let wasSelected = false;

    if (this.selected && vertices.length > 0) {
      this.selected = false;
      wasSelected = true;
    }

    if (wasSelected || this.inMultiSelected !== inMultiSelected) {
      // Only update if needed.
      this.inMultiSelected = inMultiSelected;
      this.repaint();
    }
  }

  onDispatcherEvent(action: WorkflowGraphDispatcherAction) {
    switch (action.type) {

      // case ActionType.MultiSelectVertex:
      //   this.selected = null;
      //   this.repaint();
      //   break;

      case ActionType.SelectVertex:
        this.selectIfApplicable(action.data);
        break;

      case ActionType.VertexMoved:
      case ActionType.VertexMoving:

        if (action.originator !== this) {
          this.repaint();
        }

        break;

      case ActionType.VertexUpdated:
        if (this.vertex === action.data) {
          this.repaint();
        }
        break;
    }
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();

    this.dragSupport.deactivate();

    if (this.dragFinish$) {
      this.dragFinish$.unsubscribe();
      this.dragFinish$ = null;
    }

    if (this.dragMove$) {
      this.dragMove$.unsubscribe();
      this.dragMove$ = null;
    }

    if (this.selectionUpdated$) {
      this.selectionUpdated$.unsubscribe();
      this.selectionUpdated$ = null;
    }
  }

  ngOnInit(): void {
    super.ngOnInit();

    if (this.graphSelectionService.isSelected(this.vertex)) {
      // If its there, select it.
      this.inMultiSelected = true;
    }
  }

  get vis(): WorkflowVertexVisualization {
    return this.specifications.visualization;
  }

  ngOnChanges() {
    if (this.vertex) {
      this.specifications = this.graphService.getLibrary().get(this.vertex.identifier);
    }
  }

  foregroundOverride(color: string): string {
    return this.selected ? '#FFF' : color;
  }

  backgroundOverride(color: string): string {
    return this.selected ? '#f00' : (this.inMultiSelected ? '#F0F' : color);
  }

  getOutletSpecification(index: number): VertexInteractionPointSpecification {
    return this.getPointSpecification(index, VertexInteractionPointPreferredType.Outlet);
  }

  getInletSpecification(index: number): VertexInteractionPointSpecification {
    return this.getPointSpecification(index, VertexInteractionPointPreferredType.Inlet);
  }

  getPointSpecification(index: number, type: VertexInteractionPointPreferredType): VertexInteractionPointSpecification {
    const points = this.vis.shape.representation.interactionPoints
      .filter(p => !p.prefer || p.prefer === type);

    return points.length > index ? points[index] : {
      at: {x: 0, y: 0},
      location: VertexInteractionPointLocation.Center
    };
  }
}



// WEBPACK FOOTER //
// ./src/library/graph/vertex/workflow-graph-vertex.component.ts